import { MoneyDTO } from "../NormasTeachingProduct/useNormasTeachingProductById.ts";
import { NormasTeachingPromoDTO } from "../NormasTeachingPromo/useActivePromoForProduct.ts";
import { PurchasedItemSnapshotDTO } from "../PurchasedItem/PurchasedItemSnapshotDTO.ts";

export type CalendarDate = string;

export enum BonificoOrderStatus {
  Draft = "draft",
  Discarded = "discarded",
  PendingPayment = "pending_payment",
  ManuallyHandled = "manually_handled",
  PartiallyPaid = "partially_paid",
  AwaitingInvoice = "awaiting_invoice",
  Paid = "paid",
  Historical = "historical",
  Refunded = "refunded",
  PartiallyRefunded = "partially_refunded",
}

export enum VatExemption {
  Article7Ter = "article_7_ter",
}

export type BonificoCustomer = {
  email: string;
  firstName: string;
  lastName: string;
  taxCode?: string;
};

export type BonificoParticipant = {
  email: string;
  firstName: string;
  lastName: string;
};

export type BonificoBundleParticipant = {
  customer: BonificoCustomer;
  products: { id: string; priceInCents: MoneyDTO }[];
};

export type BonificoBusinessInformation = {
  businessName: string;
  businessAddress: {
    street: string;
    postalCode: string;
    city: string;
    province: string;
    country?: string;
  };
  vatCode: string;
  sdiCode?: string;
  pec?: string;
  businessType: "Person" | "Company" | "IndividualCompany" | "Unknown";
};

export type BonificoOrderPricingDTO = {
  listPriceInCents: MoneyDTO;
  couponDiscountInCents?: MoneyDTO | null;
  isAmountManuallySet: boolean;
  amountPaidInCents: MoneyDTO;
  bundleProducts?:
    | {
        participantEmail: string;
        productId: string;
        listPriceInCents: MoneyDTO;
        amountPaidInCents: MoneyDTO;
        promos?: Pick<NormasTeachingPromoDTO, "_id" | "bonuses">[] | null;
      }[]
    | null;
};

export type BonificoInstallmentsSettlementDTO = {
  gatewayOrderId: string;
  paymentGateway: string;
  numberOfInstallments: number;
  totalAmountInCents: { amount: number; currency: string };
  amountAlreadyPaidInCents: { amount: number; currency: string };
  purchasedItem?: PurchasedItemSnapshotDTO | null;
  settledAtUtc: string;
};

export enum ExternalSettlementGateway {
  SamCart = "samcart",
  Stripe = "stripe",
  HeyLight = "heylight",
  Other = "other",
}

export enum ExternalOrderPaymentType {
  Installments = "installments",
  OneTime = "one_time",
}

export const GATEWAYS_SELLING_CATALOG_PRODUCTS = [
  ExternalSettlementGateway.Stripe,
  ExternalSettlementGateway.HeyLight,
];

export type BonificoExternalSettlementDTO = {
  gateway: ExternalSettlementGateway;
  otherGatewayName?: string | null;
  externalOrderId: string;
  externalProductId?: string | null;
  originalPaymentType: ExternalOrderPaymentType;
  numberOfInstallments?: number | null;
  totalAmountInCents: { amount: number; currency: string };
  amountAlreadyPaidInCents: { amount: number; currency: string };
};

const GATEWAY_LABELS: Record<
  Exclude<ExternalSettlementGateway, ExternalSettlementGateway.Other>,
  string
> = {
  [ExternalSettlementGateway.SamCart]: "SamCart",
  [ExternalSettlementGateway.Stripe]: "Stripe",
  [ExternalSettlementGateway.HeyLight]: "HeyLight",
};

export function externalSettlementGatewayLabel(
  settlement: Pick<
    BonificoExternalSettlementDTO,
    "gateway" | "otherGatewayName"
  >,
): string {
  return settlement.gateway === ExternalSettlementGateway.Other
    ? settlement.otherGatewayName || "gateway esterno"
    : GATEWAY_LABELS[settlement.gateway];
}

export function externalSettlementTitle(
  settlement: Pick<
    BonificoExternalSettlementDTO,
    "gateway" | "otherGatewayName" | "originalPaymentType"
  >,
): string {
  return `${externalSettlementKindLabel(settlement)} ${externalSettlementGatewayLabel(settlement)}`;
}

export function externalSettlementKindLabel(
  settlement: Pick<BonificoExternalSettlementDTO, "originalPaymentType">,
): string {
  return settlement.originalPaymentType === ExternalOrderPaymentType.OneTime
    ? "Integrazione"
    : "Saldo rate";
}

export type BonificoB2BPaymentDTO = {
  paymentId: string;
  amountInCents: { amount: number; currency: string };
  valueDateUtc: string;
  registeredAtUtc: string;
};

export type BonificoB2BCourseGrantDTO = {
  grantId: string;
  beneficiary: BonificoParticipant;
  productId: string;
  productSnapshot: {
    productClass: string;
    productName: string;
    variantProductName: string;
  };
  grantedAtUtc: string;
};

export type BonificoB2BContractDTO = {
  contractDateUtc: string;
  contractAmountInCents: { amount: number; currency: string };
  payments: BonificoB2BPaymentDTO[];
  courseGrants?: BonificoB2BCourseGrantDTO[] | null;
};

export type BonificoOrderDTO = {
  _id: string;
  orderId: string;
  status: BonificoOrderStatus;
  invoicedOnPayment?: boolean | null;
  normasteachingProductId?: string | null;
  productType?: "product" | "bundle" | null;
  amountPaidInCents?: MoneyDTO | null;
  customer: BonificoCustomer;
  participants?: BonificoParticipant[] | null;
  bundleParticipants?: BonificoBundleParticipant[] | null;
  businessInformation?: BonificoBusinessInformation | null;
  coupon?: { _id: string; couponCode: string; value: number } | null;
  pricing?: BonificoOrderPricingDTO | null;
  oneTimeNumberOfInstallments?: number | null;
  termsAcceptance?: {
    privacyPolicyAcceptedAtUtc: string;
    additionalTermsAcceptedAtUtc: string;
  } | null;
  receiptDateUtc?: string | null;
  confirmedAtUtc?: string | null;
  valueDateUtc?: string | null;
  paidAtUtc?: string | null;
  invoiceIssuedAtUtc?: string | null;
  vatExemption?: VatExemption | null;
  invoiceVatExemption?: VatExemption | null;
  invoiceItemDescription?: string | null;
  notes?: string | null;
  notesUpdatedAtUtc?: string | null;
  refundedAmountInCents?: MoneyDTO | null;
  refundedAtUtc?: string | null;
  createdAt: string;
  productSnapshot?: PurchasedItemSnapshotDTO | null;
  installmentsSettlement?: BonificoInstallmentsSettlementDTO | null;
  externalSettlement?: BonificoExternalSettlementDTO | null;
  b2bContract?: BonificoB2BContractDTO | null;
};

export function b2bCompanyNameOf(order: BonificoOrderDTO): string | undefined {
  if (!order.b2bContract) return undefined;
  return order.businessInformation?.businessName?.trim() || undefined;
}

export function b2bAmountPaidInCents(contract: BonificoB2BContractDTO): number {
  return contract.payments.reduce(
    (sum, payment) => sum + payment.amountInCents.amount,
    0,
  );
}

export function b2bRemainingAmountInCents(
  contract: BonificoB2BContractDTO,
): number {
  return contract.contractAmountInCents.amount - b2bAmountPaidInCents(contract);
}

export function purchasedItemSnapshotOf(
  order: BonificoOrderDTO,
): PurchasedItemSnapshotDTO | undefined {
  return (
    order.productSnapshot ??
    order.installmentsSettlement?.purchasedItem ??
    undefined
  );
}

export type BonificoOrderItemPayload = {
  productType: "product" | "bundle";
  normasteachingProductId: string;
  participants?: BonificoParticipant[];
  bundleParticipants?: BonificoParticipant[];
  couponId?: string;
  amountOverrideInCents?: number;
};

export type ConfirmBonificoOrderPayload = BonificoOrderItemPayload & {
  customer: BonificoCustomer;
  businessInformation: BonificoBusinessInformation;
  receiptDate: CalendarDate;
  vatExemption?: VatExemption;
  invoiceItemDescription?: string | null;
};

export type CreateHistoricalBonificoOrderPayload = BonificoOrderItemPayload & {
  customer: BonificoCustomer;
  businessInformation?: BonificoBusinessInformation;
  valueDate: CalendarDate;
};

export type B2BCourseGrantPayload = {
  beneficiary: BonificoParticipant;
  productId: string;
};

export type CreateB2BBonificoOrderPayload = {
  customer: BonificoCustomer;
  businessInformation: BonificoBusinessInformation;
  contractDate: CalendarDate;
  contractAmountInCents: number;
  courseGrants?: B2BCourseGrantPayload[];
};

export type CreateExternalSettlementPayload = {
  gateway: ExternalSettlementGateway;
  otherGatewayName?: string;
  externalOrderId: string;
  externalProductId?: string;
  normasteachingProductId: string;
  customer: BonificoCustomer;
  businessInformation: BonificoBusinessInformation;
  receiptDate: CalendarDate;
  originalPaymentType: ExternalOrderPaymentType;
  numberOfInstallments?: number;
  totalAmountInCents: number;
  amountAlreadyPaidInCents: number;
  settlementAmountInCents: number;
  vatExemption?: VatExemption;
  invoiceItemDescription?: string | null;
};

export type CreateBonificoDraftPayload = {
  customer: BonificoCustomer;
  businessInformation: BonificoBusinessInformation;
  analytics?: { utmCampaign: string; utmMedium: string; utmSource: string };
  privacyPolicyAccepted: true;
  additionalTermsAccepted: true;
};

export type BulkInvoiceIssueResultDTO = {
  issuedOrderIds: string[];
  skippedOrderIds: string[];
  notIssuedOrderIds: string[];
  failure?: { orderId: string; message: string } | null;
};

export function getBonificoOrderErrorMessage(
  e: unknown,
  fallback: string,
): string {
  const err = e as { response?: { data?: { message?: string } } };
  return err.response?.data?.message ?? fallback;
}
